import mixpanel from "mixpanel-browser";
import { useContext, createContext, useState, useRef } from "react";

import { SCRIBENOTE_WS, WS_RECONNECT_WAIT } from "../constants";

const WSContext = createContext();

export const useWS = () => {
  return useContext(WSContext);
};

const MAX_RECONNECTS = 3;

function scheduleReconnect(
  channel,
  reconnectCountRef,
  reconnectTimersRef,
  connectFn,
) {
  const count = (reconnectCountRef.current[channel] ?? 0) + 1;
  if (count > MAX_RECONNECTS) return;

  reconnectCountRef.current[channel] = count;
  const jitter = Math.random() * WS_RECONNECT_WAIT;
  const backoff = count ** 2 * WS_RECONNECT_WAIT;
  const timerId = setTimeout(() => {
    reconnectTimersRef.current.delete(timerId);
    connectFn(channel);
  }, backoff + jitter);
  reconnectTimersRef.current.add(timerId);
}

export function useProvideWS() {
  const [webSockets, setWebSockets] = useState({});
  const connectionsRef = useRef({});
  const reconnectCountRef = useRef({});
  const reconnectTimersRef = useRef(new Set());

  const connectWS = (channel, attemptReconnect = true) => {
    const existing = connectionsRef.current[channel];
    if (existing?.readyState === WebSocket.CONNECTING) return null;
    if (existing?.readyState === WebSocket.OPEN) return existing;

    let ws;
    try {
      ws = new WebSocket(`${SCRIBENOTE_WS}/ws/${channel}`);
    } catch (e) {
      mixpanel.track("Websocket Error", { channel });
      return null;
    }

    connectionsRef.current[channel] = ws;

    const handleClose = () => {
      if (!connectionsRef.current[channel]) return;
      connectionsRef.current[channel] = null;
      setWebSockets((prev) => ({ ...prev, [channel]: null }));
      if (attemptReconnect) {
        scheduleReconnect(
          channel,
          reconnectCountRef,
          reconnectTimersRef,
          connectWS,
        );
      }
    };

    ws.addEventListener("open", () => {
      connectionsRef.current[channel] = ws;
      setWebSockets((prev) => ({ ...prev, [channel]: ws }));
      reconnectCountRef.current[channel] = 0;
    });
    ws.addEventListener("error", handleClose);
    ws.addEventListener("close", handleClose);

    return ws;
  };

  const disconnectAll = () => {
    for (const timerId of reconnectTimersRef.current) {
      clearTimeout(timerId);
    }
    reconnectTimersRef.current.clear();

    for (const [channel, ws] of Object.entries(
      connectionsRef.current,
    )) {
      if (ws) {
        connectionsRef.current[channel] = null;
        ws.close();
      }
    }
    reconnectCountRef.current = {};
    setWebSockets({});
  };

  return {
    connectWS,
    disconnectAll,
    webSockets,
  };
}

export function WSProvider({ children }) {
  const webSockets = useProvideWS();
  return (
    <WSContext.Provider value={webSockets}>
      {children}
    </WSContext.Provider>
  );
}
